import axiosInstance, { axiosInstanceNoToast } from '../../../../fetchConfig/axiosConfig';
import { formatCourtDateDisplayDay, parseCourtDateCalendarKey } from '../../../../common/dateUtils.js';
import { EVIDENCE_TYPE_TO_REGEN_OPTIONS } from '../../../../common/mappings.js';
import { usePersistedDataStore } from '../../../store.js';
import logger from '../../../../utils/logger';
import { regenerateWorkups } from '../../../../services/regenerateWorkupsService.js';
import { validateOfferEmailWorkflow } from '../../EmailFactory/Validations/offerEmailValidations.js';
import { prepareOffersEmail } from '../../EmailFactory/emailFactory';
import { EMAIL_TEMPLATE_TYPES, generateEmailPreview, sendEmail } from '../../EmailFactory/emailService';
import { buildTownAccessLink } from './TownLinkConstruction.js';
import { buildLinkOptions, buildPermissionStateFromSaved } from './townPortalLinkPermissions.js';

const TOWN_PORTAL_WORKUP_EVIDENCE_TYPES = ['Unequal', 'Excessive'];

export const TOWN_PORTAL_DELIVERY_METHODS = {
  LINK: 'link',
  SPREADSHEET: 'spreadsheet',
};

// --- Counterparty / town portal link ---

const normalizeLinkEmails = (email) => {
  const raw = Array.isArray(email) ? email : email ? [email] : [];
  const seen = new Set();
  return raw
    .map((e) => String(e || '').trim())
    .filter((e) => {
      if (!e) return false;
      const key = e.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const normalizeLinkCourtDate = (courtDate) => {
  if (!courtDate || courtDate === 'All') return null;
  return parseCourtDateCalendarKey(courtDate);
};

const validateLinkParams = ({ email, muniCode, taxYear }) => {
  const emails = normalizeLinkEmails(email);
  if (!emails.length || !muniCode?.trim() || !taxYear) {
    return {
      isValid: false,
      error: 'At least one email, muni code, and tax year are required to generate a link.',
    };
  }
  return { isValid: true, error: null };
};

/** Link API payload from persisted negotiation context + send-time email/options. */
const buildCounterpartyLinkRequestParams = ({ email, user, linkOptions } = {}) => {
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const courtDateRaw =
    negotiationObj?.CourtDate === 'All' ? null : negotiationObj?.CourtDate;

  return {
    email: normalizeLinkEmails(email),
    muni_code: negotiationObj?.MuniCode,
    tax_year: negotiationObj?.TaxYear,
    court_date: normalizeLinkCourtDate(courtDateRaw),
    user_id: user?.userId ?? null,
    village_run: negotiationObj?.villageRun ?? 0,
    ...buildLinkOptions(linkOptions),
  };
};

/** Generate a magic-link URL for town portal access. */
const generateCounterpartyLink = async ({ email, user, linkOptions } = {}) => {
  const linkParams = buildCounterpartyLinkRequestParams({ email, user, linkOptions });

  const validation = validateLinkParams({
    email: linkParams.email,
    muniCode: linkParams.muni_code,
    taxYear: linkParams.tax_year,
  });

  if (!validation.isValid) {
    return { success: false, error: validation.error };
  }

  try {
    const response = await axiosInstance.post('/auth/counterparty-link', linkParams);
    if (!response.data?.success || !response.data?.magic_token) {
      return {
        success: false,
        error: response.data?.error || 'Failed to generate counterparty link.',
      };
    }

    const accessLink = buildTownAccessLink(response.data.magic_token);

    return {
      success: true,
      accessLink,
    };
  } catch (err) {
    logger.error('Error generating counterparty link:', err);
    if (err.response?.status === 401) {
      return { success: false, error: 'Authentication required to generate counterparty links.' };
    }
    if (err.response?.data?.error) {
      return { success: false, error: err.response.data.error };
    }
    return { success: false, error: 'Failed to generate counterparty link. Please try again.' };
  }
};

// --- Pre-dialog validation (beta menu: validate before TownPortalOffersDialog) ---

const mapValidationToUi = (validation) => {
  if (validation.isValid) {
    return { canOpenDialog: true };
  }

  switch (validation.validationType) {
    case 'missing_offers':
      return {
        canOpenDialog: false,
        requiresMissingOffersDialog: true,
        missingOffers: validation.missingOffers || [],
        error: validation.error,
      };
    case 'missing_workup_values':
      return {
        canOpenDialog: false,
        requiresMissingWorkupDialog: true,
        missingWorkupValues: validation.missingWorkupValues || [],
        error: validation.error,
      };
    case 'subset_warning':
      return {
        canOpenDialog: false,
        requiresSubsetWarning: true,
        openCases: validation.openCasesNotSelected || [],
        error: validation.error,
      };
    case 'workups_above_ifmv':
      return {
        canOpenDialog: false,
        requiresWorkupsAboveIfmvDialog: true,
        workupsAboveIfmv: validation.workupsAboveIfmv || [],
        error: validation.error,
      };
    default:
      return {
        canOpenDialog: false,
        error: validation.error || 'Cannot send offers for the current selection.',
      };
  }
};

/** Load saved portal link permission flags for muni + tax year (+ court date). */
export const fetchCounterpartyLinkDefaults = async ({ negotiationObj } = {}) => {
  const muniCode = negotiationObj?.MuniCode;
  const taxYear = negotiationObj?.TaxYear;
  if (!muniCode || taxYear == null || taxYear === '') {
    return { success: false, found: false, error: 'MuniCode and TaxYear are required' };
  }

  const courtDateRaw = negotiationObj?.CourtDate === 'All' ? null : negotiationObj?.CourtDate;
  const body = { MuniCode: muniCode, TaxYear: taxYear };
  const courtDate = normalizeLinkCourtDate(courtDateRaw);
  if (courtDate) body.CourtDate = courtDate;

  try {
    const { data } = await axiosInstanceNoToast.post('/fetch_counterparty_link_defaults', body);
    if (!data?.success) {
      return { success: false, found: false, error: data?.error || 'Failed to load portal link defaults.' };
    }
    if (!data.found) {
      return { success: true, found: false, permissionState: buildPermissionStateFromSaved() };
    }
    return {
      success: true,
      found: true,
      permissionState: buildPermissionStateFromSaved(data),
    };
  } catch (err) {
    logger.error('fetchCounterpartyLinkDefaults failed:', err);
    return {
      success: false,
      found: false,
      error: err.response?.data?.error || 'Failed to load portal link defaults.',
    };
  }
};

/** Run full offer validations before opening the town portal dialog. */
export const initiateTownPortalOffersFlow = ({
  selectedRows,
  allRows,
  negotiationObj,
  user,
  skipSubsetCheck = false,
  skipWorkupsAboveIfmvCheck = false,
}) => {
  const validation = validateOfferEmailWorkflow({
    selectedRows,
    allRows,
    negotiationObj,
    user,
    skipSubsetCheck,
    skipWorkupsAboveIfmvCheck,
  });
  return mapValidationToUi(validation);
};

// --- Town portal email template (link injected at send time) ---

/** Href placeholder in preview HTML; replaced with the real URL when the user sends. */
const TOWN_PORTAL_LINK_PLACEHOLDER = '__TOWN_PORTAL_ACCESS_LINK__';

const escapeHtmlAttr = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/** e.g. "Aventine Offers - Andes 6/3/2026" (court-date runs append hearing date). */
const buildTownPortalEmailSubject = (shareWorkups = false) => {
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const muni = (negotiationObj?.muniString ?? '').trim() || 'Town';
  const rawCourt = negotiationObj?.CourtDate === 'All' ? null : negotiationObj?.CourtDate;
  const courtLabel = rawCourt ? formatCourtDateDisplayDay(rawCourt) : '';
  const courtPart = courtLabel ? ` ${courtLabel}` : '';
  const prefix = shareWorkups ? 'Aventine Offers and Workups' : 'Aventine Offers';
  return `${prefix} - ${muni}${courtPart}`;
};

/** HTML body for town portal email; href is a placeholder until send-time link generation. */
const buildTownPortalEmailHtml = (caseInfo) => {
  const openCases = caseInfo?.openCases ?? '';
  const settledCases = caseInfo?.settledCases ?? '';
  const safeHref = escapeHtmlAttr(TOWN_PORTAL_LINK_PLACEHOLDER);

  return `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px;">
  <p style="margin: 0 0 12px 0;">We have new offers available for your review. You can access this information using the secure town portal link below.</p>
  <div style="background-color: #e8f4fd; border: 2px solid #3498db; border-radius: 8px; padding: 20px; margin: 20px 0; text-align: center;">
    <p style="margin: 0 0 8px 0; color: #2c3e50; font-weight: bold;">Town Access</p>
    <p style="margin: 0 0 16px 0; font-size: 14px; color: #555;">Open cases: ${escapeHtmlAttr(openCases)} &nbsp;|&nbsp; Settled: ${escapeHtmlAttr(settledCases)}</p>
    <a href="${safeHref}" style="background-color: #1976d2; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">Open Town Portal</a>
  </div>
  <p style="margin: 16px 0 0 0; font-size: 14px;">Please reply to this email if you have any questions.</p>
  <p style="margin: 24px 0 0 0; font-size: 14px;">Sincerely,<br>Aventine Properties</p>
</div>`;
};

const injectTownPortalAccessLink = (emailBody, accessLink) => {
  if (!emailBody?.includes(TOWN_PORTAL_LINK_PLACEHOLDER)) {
    return {
      success: false,
      error:
        'The email body no longer contains the town portal link. Reset the message or reopen the composer.',
    };
  }
  return {
    success: true,
    emailBody: emailBody.split(TOWN_PORTAL_LINK_PLACEHOLDER).join(accessLink),
  };
};

/**
 * Load assessor recipients and render email preview (portal link is created on send, not here).
 */
export const prepareTownPortalEmailPreview = async ({ parcelIds, linkOptions, user }) => {
  try {
    const offersResult = await prepareOffersEmail(parcelIds);

    const preview = await generateEmailPreview({
      templateType: EMAIL_TEMPLATE_TYPES.OFFERS,
      data: offersResult.data,
      caseInfo: offersResult.emailOptions.caseInfo,
      user,
    });

    if (!preview.success) {
      return { success: false, error: preview.error || 'Failed to load assessor email addresses' };
    }

    const toEmails = preview.toEmails || [];
    const assessorEmails = Array.isArray(preview.assessor_email)
      ? preview.assessor_email
      : preview.assessor_email
        ? [preview.assessor_email]
        : [];
    const emailSuggestions = [...new Set([...toEmails, ...assessorEmails].filter(Boolean))];

    if (!normalizeLinkEmails(toEmails).length) {
      return {
        success: false,
        error: 'No assessor email found for this municipality. Add a recipient or check assessor records.',
      };
    }

    const caseInfo = offersResult.emailOptions.caseInfo;
    const shareWorkups = Boolean(linkOptions?.share_workups);
    const subject = buildTownPortalEmailSubject(shareWorkups);
    const emailBody = buildTownPortalEmailHtml(caseInfo);

    return {
      success: true,
      toEmails,
      ccEmails: user?.email ? [user.email] : [],
      subject,
      emailBody,
      emailSuggestions,
      emailData: {
        templateType: EMAIL_TEMPLATE_TYPES.OFFERS,
        data: offersResult.data,
        caseInfo,
        linkOptions,
        user,
      },
    };
  } catch (err) {
    logger.error('prepareTownPortalEmailPreview failed:', err);
    return { success: false, error: err.message || 'Failed to prepare town portal email' };
  }
};

/** Fire-and-forget workup regeneration for parcels included in a town portal send. */
const regenerateWorkupsAfterTownPortalSend = (parcelIds, userId) => {
  const ids = [...new Set((parcelIds || []).map((id) => String(id || '').trim()).filter(Boolean))];
  if (!ids.length) return;

  void (async () => {
    try {
      const { negotiationObj } = usePersistedDataStore.getState();
      const isVillageRun = negotiationObj?.villageRun === 1;

      await Promise.all(
        TOWN_PORTAL_WORKUP_EVIDENCE_TYPES.map((evidenceType) => {
          const regenOpts = EVIDENCE_TYPE_TO_REGEN_OPTIONS[evidenceType];
          if (!regenOpts) return Promise.resolve();
          return regenerateWorkups({
            parcelIds: ids,
            userId,
            ...regenOpts,
            ...(isVillageRun ? { reportType: 'village' } : {}),
            suppressToasts: true,
          });
        }),
      );
    } catch (err) {
      logger.error('Background workup regeneration after town portal send failed:', err);
    }
  })();
};

/** Send town portal email: generate portal link at send time, then inject into the composed body. */
export const sendTownPortalEmailFromPreview = async ({ emailPreviewData }) => {
  const { toEmails, ccEmails, bccEmails, subject, emailBody, emailData } = emailPreviewData;

  if (!toEmails?.length) {
    return { success: false, error: 'At least one recipient email is required' };
  }

  if (!emailBody?.trim()) {
    return { success: false, error: 'Email body is required' };
  }

  const linkEmails = normalizeLinkEmails(toEmails);
  if (!linkEmails.length) {
    return {
      success: false,
      error: 'At least one valid recipient email is required to generate the portal link',
    };
  }

  const linkResult = await generateCounterpartyLink({
    email: linkEmails,
    user: emailData?.user,
    linkOptions: emailData?.linkOptions,
  });

  if (!linkResult.success) {
    return { success: false, error: linkResult.error };
  }

  const injected = injectTownPortalAccessLink(emailBody, linkResult.accessLink);
  if (!injected.success) {
    return { success: false, error: injected.error };
  }

  const sendResult = await sendEmail({
    templateType: EMAIL_TEMPLATE_TYPES.OFFERS,
    data: emailData.data,
    fileUrl: linkResult.accessLink,
    toEmails,
    ccEmails: ccEmails || [],
    bccEmails: bccEmails || [],
    // fallback proects if a user deletes the full subject for some dumb reason.
    subject: subject || buildTownPortalEmailSubject(Boolean(emailData?.linkOptions?.share_workups)),
    emailBody: injected.emailBody,
    user: emailData.user,
    caseInfo: emailData.caseInfo,
  });

  if (sendResult.success) {
    const parcelIds = (emailData.data || [])
      .map((row) => row.parcel_id)
      .filter(Boolean);
    regenerateWorkupsAfterTownPortalSend(parcelIds, emailData.user?.userId);
  }

  return sendResult;
};
